import { useTranslations } from "use-intl";

import { curatedCrateDecadeLabel } from "@/components/curated-crate-name";
import { CrateCard } from "@/features/garimpos/crate-card";
import type { CuratedCrateProof } from "@/features/garimpos/garimpos-query";
import {
  garimposDecadeId,
  type GarimposTimelineDecade,
} from "@/features/garimpos/garimpos-timeline";

/**
 * Top of a decade node: centred on the first line of the decade heading (half of the heading's
 * line box minus half the 0.875rem node), per heading size. Set on the spine container so the
 * spine can start at the first node's centre.
 */
export const DECADE_NODE_TOP_CLASS =
  "[--decade-node-top:17px] md:[--decade-node-top:23px] lg:[--decade-node-top:29px]";

/** One decade on the spine: its node, display heading, one-line note and crate grid. */
export function DecadeSection({ decade }: { decade: GarimposTimelineDecade<CuratedCrateProof> }) {
  const t = useTranslations("garimpos");
  const id = garimposDecadeId(decade.startYear);

  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="relative scroll-mt-36 pb-12 md:scroll-mt-24 md:pb-14"
    >
      <span
        aria-hidden="true"
        className="absolute top-[var(--decade-node-top)] -left-[calc(1.5rem+6px)] size-3.5 rounded-full bg-primary ring-4 ring-background sm:-left-[calc(2rem+6px)]"
      />
      <h2
        id={`${id}-title`}
        className="text-5xl font-bold leading-none tracking-[-0.035em] md:text-6xl lg:text-7xl"
      >
        {t("decadeHeading", { decade: curatedCrateDecadeLabel(decade.startYear) })}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t(`decadeNotes.${decade.startYear}`)}</p>
      <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {decade.entries.map(({ crate, proof }) => (
          <li key={crate.id}>
            <CrateCard crate={crate} proof={proof} />
          </li>
        ))}
      </ul>
    </section>
  );
}
